// src/components/SessionWarningDialog.tsx
import React, { useState, useEffect, useRef } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Typography 
} from '@mui/material';
import { useAuth } from '../AuthContext';
import { tokenManager } from '../TokenManager';

export function SessionWarningDialog() {
  const [open, setOpen] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);
  const { user, logout } = useAuth();
  const countdownRef = useRef(null);

  const stopCountdown = () => {
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
  };

  const startCountdown = (initialSeconds) => {
    stopCountdown();
    setSecondsRemaining(initialSeconds);
    countdownRef.current = setInterval(() => {
      setSecondsRemaining(prev => {
        if (prev <= 1) {
          stopCountdown();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  // Auto-logout when countdown expires
  useEffect(() => {
    if (open && secondsRemaining === 0) {
      handleLogout();
    }
    // handleLogout is stable — intentionally omitted from deps to avoid loop
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, secondsRemaining]);

  useEffect(() => {
    if (!user) {
      setOpen(false);
      stopCountdown();
      return;
    }

    const checkSession = () => {
      if (tokenManager.isSessionExpiringSoon()) {
        const secs = tokenManager.getSecondsUntilExpiry();
        setOpen(prev => {
          if (!prev) {
            // Dialog is newly opening — start a fresh countdown
            startCountdown(secs);
          }
          return true;
        });
      } else {
        // Token is healthy (Firebase may have silently refreshed it) — dismiss stale dialog
        setOpen(false);
        stopCountdown();
      }
    };

    const unsubscribe = tokenManager.addListener(() => {
      checkSession();
    });

    checkSession();

    const interval = setInterval(checkSession, 60000);

    return () => {
      clearInterval(interval);
      unsubscribe();
      stopCountdown();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleExtend = async () => {
    try {
      await tokenManager.getToken(true);
      tokenManager.restartSessionTimers();
      stopCountdown();
      setOpen(false);
    } catch (error) {
      console.error('Error extending session:', error);
      if (error.code === 'auth/user-token-expired') {
        await logout();
      }
    }
  };

  const handleLogout = async () => {
    try {
      stopCountdown();
      setOpen(false);
      await logout();
    } catch (error) {
      console.error('Error during logout:', error);
    }
  };

  const formatTime = (totalSeconds) => {
    const m = Math.floor(totalSeconds / 60);
    const s = totalSeconds % 60;
    return m > 0
      ? `${m}:${String(s).padStart(2, '0')}`
      : `${s}s`;
  };

  return (
    <Dialog 
      open={open}
      onClose={() => {}} 
    >
      <DialogTitle>Session Expiring Soon</DialogTitle>
      <DialogContent>
        <Typography>
          Your session will expire in{' '}
          <strong>{formatTime(secondsRemaining)}</strong>. Would you like to
          extend it?
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleLogout} color="error">
          Logout
        </Button>
        <Button onClick={handleExtend} color="primary" autoFocus>
          Extend Session
        </Button>
      </DialogActions>
    </Dialog>
  );
}
